import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { serviceCityHeadScripts } from "@/lib/cityJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { getCityHub, CITY_HUBS, type CityHub } from "@/data/cityHubs";
import beforeImg from "@/assets/recover-awning-green-canvas-before-san-antonio.jpg";
import hero from "@/assets/awning-blue-striped-fabric.jpg";

import heroPicture from "@/assets/awning-blue-striped-fabric.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/recover-awnings/in/$city")({
  beforeLoad: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
  },
  loader: ({ params }) => ({ hub: getCityHub(params.city)! }),
  head: (ctx) => {
    const hub = getCityHub(ctx.params.city);
    if (!hub) return {};
    const title = `Awning Recover in ${hub.name}, TX — Recover Existing Frames & Canopies | Texas Made Shade`;
    const description = `${hub.name} awning recover company. We recover existing awning and canopy frames in ${hub.name} and ${hub.region} with new Sunbrella® and Tempotest® canvas — patterned, sewn and re-installed by our Seguin, TX shop. Save 50%+ vs. a full replacement.`;
    return {
      links: [canonicalLink(`/recover-awnings/in/${hub.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: beforeImg,
        imageAlt: `Awning recover project in ${hub.name}, TX — existing frame, brand-new Sunbrella canvas`,
        socialTitle: `Recover Awning & Canopy — ${hub.name}, TX`,
        socialDescription: description,
        keywords: `recover awning ${hub.name}, awning recover ${hub.name}, ${hub.name} awning recover company, canopy recover ${hub.name}, awning fabric replacement ${hub.name}, sunbrella recover ${hub.name}, ${hub.region} awning recover`,
      }),
      scripts: [
        ...serviceCityHeadScripts({
          path: `/recover-awnings/in/${hub.slug}`,
          serviceName: "Awning & Canopy Recovers",
          city: hub.name,
          region: hub.region,
          parentPath: "/recover-awnings",
          parentName: "Awning Recovers",
        }),
        ...serviceHeadScripts({
          path: `/recover-awnings/in/${hub.slug}`,
          serviceName: `Awning & Canopy Recover in ${hub.name}, TX`,
          breadcrumbName: `Awning Recover in ${hub.name}`,
          description,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Awning & Canopy Recover in ${hub.name}, TX`,
            serviceType: "Awning Recover",
            url: canonicalUrl(`/recover-awnings/in/${hub.slug}`),
            provider: {
              "@type": "LocalBusiness",
              name: "Texas Made Shade",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Seguin",
                addressRegion: "TX",
                addressCountry: "US",
              },
            },
            areaServed: {
              "@type": "City",
              name: hub.name,
              containedInPlace: { "@type": "AdministrativeArea", name: hub.region },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-navy-deep">City not found</h1>
      <p className="mt-4 text-navy-deep/75">
        We don't have a dedicated awning recover page for that city yet.{" "}
        <Link to="/recover-awnings" className="text-red underline">See our main awning recover service</Link>.
      </p>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-navy-deep">Something went wrong</h1>
      <p className="mt-4 text-navy-deep/75">{(error as Error)?.message}</p>
    </main>
  ),
  component: RecoverAwningsCityPage,
});

function RecoverAwningsCityPage() {
  const { hub } = Route.useLoaderData() as { hub: CityHub };
  const nearby = hub.nearby
    .map((s: string) => getCityHub(s))
    .filter((c): c is CityHub => Boolean(c));

  return (
    <ServicePage
      currentPath={`/recover-awnings/in/${hub.slug}`}
      serviceName={`Awning & Canopy Recover in ${hub.name}`}
      breadcrumbs={[
        { label: "Awnings", to: "/awnings" },
        { label: "Awning & Canopy Recover", to: "/recover-awnings" },
        { label: `${hub.name}, TX` },
      ]}
      eyebrow={`★ AWNING RECOVER · ${hub.name.toUpperCase()}, TX ★`}
      title={`Recover Your Existing Awning Frame in ${hub.name}, TX`}
      intro={`We recover awnings and canopies on existing frames throughout ${hub.name} and ${hub.region}. New Sunbrella® or Tempotest® canvas — hand-patterned to your frame, sewn in our Seguin, TX shop, and re-installed by the same crew. A fraction of the cost of a full replacement.`}
      heroImage={heroPicture}
      heroCta={{
        eyebrow: `Awning Recover Company · Serving ${hub.name}, TX`,
        headline: `${hub.name} Awning Recover — Get a Free On-Site Estimate`,
        subhead: `Send us a few photos and rough measurements of your existing awning or canopy in ${hub.name}. We'll come back same business day with a written, no-pressure recover estimate.`,
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Awning / Canopy Recover")}&notes=${encodeURIComponent(`Awning recover inquiry — project in ${hub.name}, TX. Please include rough opening size, existing frame material, and current canvas color.`)}`,
        primaryLabel: `Get My Free ${hub.name} Recover Quote`,
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        `Awning & canopy recover service in ${hub.name}, TX`,
        "Sunbrella® and Tempotest® premium acrylic canvas",
        "Hand-patterned on-site for a perfect fit to your frame",
        "Hundreds of solid colors, stripes and patterns",
        "Save 50%+ vs. a full new awning",
        `Serving ${hub.name}, ${hub.region} and surrounding Texas`,
      ]}
      body={[
        {
          heading: `Why Recover an Awning in ${hub.name} Instead of Replacing It?`,
          text: `If your aluminum or steel awning frame is structurally sound, there's no reason to scrap it. The fabric is what fails first under ${hub.region.includes("Coast") ? "the Gulf Coast sun and salt air" : "Texas sun"} — frames typically last 20+ years. A canvas recover gives ${hub.name} property owners a brand-new awning at a fraction of the cost.`,
        },
        {
          heading: `How an Awning Recover Works in ${hub.name}`,
          text: `We come out to your ${hub.name} property, inspect the existing frame, take exact field patterns, and pull the old canvas. Back in our Seguin shop we cut and sew the new Sunbrella or Tempotest canvas, then return to stretch and fasten it to your frame. Most ${hub.name} recovers turn around in 1–2 weeks.`,
        },
        {
          heading: `Storefront, Restaurant & Patio Recovers in ${hub.name}`,
          text: `From downtown ${hub.name} storefronts and restaurants to back-patio and porch awnings on residential homes, we recover all of it. Walkway covers, entry domes and commercial canopies throughout ${hub.region} are right in our wheelhouse.`,
        },
        {
          heading: "Frame Repairs (If Needed)",
          text: "If your frame has a bent rafter, cracked weld or rusted spot, our welders can repair it in the same visit — TIG-welded aluminum or steel — so the new canvas goes onto a frame that's ready for another decade.",
        },
      ]}
      applications={[
        "Storefront Awnings",
        "Restaurant Canopies",
        "Patio Awnings",
        "Window Awnings",
        "Walkway Covers",
        "Entry Domes",
        "RV & Porch Awnings",
      ]}
      faqs={[
        {
          q: `How much does it cost to recover an awning in ${hub.name}?`,
          a: `An awning recover in ${hub.name} typically runs 40–60% of the cost of a brand-new awning of the same size, because you're keeping the existing welded frame. We give a firm written quote after a free on-site measure.`,
        },
        {
          q: `Do you recover awnings in ${hub.name}, TX?`,
          a: `Yes — we recover storefront, restaurant, patio and commercial canopies throughout ${hub.name} and the rest of ${hub.region} from our shop in Seguin, TX.`,
        },
        {
          q: "What fabric do you use for recovers?",
          a: "Sunbrella® and Tempotest® solution-dyed acrylic canvas. Both carry a 10-year color-fast warranty and are the only fabrics we trust for Texas sun.",
        },
        {
          q: "Can you match my old awning's color or stripe?",
          a: "In almost every case, yes. Sunbrella alone offers hundreds of solid colors and awning stripes. We bring fabric samples to your site so you can match — or upgrade — the original look.",
        },
        {
          q: `How long does an awning recover take in ${hub.name}?`,
          a: `Most ${hub.name} residential and small storefront recovers turn around in 1–2 weeks from the day we pattern. Larger commercial canopies may take 2–4 weeks depending on fabric lead time.`,
        },
      ]}
      cityCallouts={[
        {
          to: `/${hub.slug}` as string,
          city: hub.name,
          anchorText: `All Texas Made Shade services in ${hub.name}, TX`,
          blurb: `See every awning, cover, sail-shade and welding service we offer to ${hub.name} homes and businesses.`,
        },
        ...nearby.slice(0, 3).map((n: CityHub) => ({
          to: `/recover-awnings/in/${n.slug}` as string,
          city: n.name,
          anchorText: `Awning recovers in ${n.name}, TX`,
          blurb: `We also recover awnings and canopies in nearby ${n.name} (${n.region}).`,
        })),
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE CITIES WE RECOVER AWNINGS IN</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Awning Recovers Across Central & South Texas
              </h2>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {CITY_HUBS.filter((c) => c.slug !== hub.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/recover-awnings/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Awning Recovers in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}
